import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDate } from '../lib/format'

/**
 * CSR scholarships (part 96).
 *
 * A round is screened first, then scored against a four-part rubric, then
 * ranked. The pool is split equally among the highest-ranked applicants the
 * budget can support at the minimum award; a request below the equal share is
 * paid in full and the difference is redistributed. The final division
 * remainder is paid one satang at a time from the top of the ranking, so the
 * screen never has to explain a random-looking amount.
 */

interface CSRWeights {
  impact_bp: number
  need_bp: number
  feasibility_bp: number
  sustainability_bp: number
}

interface CSRRules {
  nationality: string
  min_age: number
  max_age: number
  max_household_income_satang: number
  min_gpa_hundredths: number
  levels: string[]
  require_enrollment: boolean
}

interface CSRProgram {
  id: string
  name: string
  description: string
  budget_satang: number
  max_award_satang: number
  min_award_satang: number
  weights: CSRWeights
  rules: CSRRules
  opens_on?: string
  closes_on?: string
  status: 'draft' | 'open' | 'closed' | 'allocated'
  allocated_satang: number
  awarded_count: number
  waitlisted_count: number
  excluded_count: number
  allocated_at?: string
  created_at: string
  updated_at: string
}

interface CSRApplication {
  id: string
  program_id: string
  reference_code: string
  full_name: string
  birth_date: string
  nationality: string
  household_income_satang: number
  gpa_hundredths: number
  study_level: string
  institution: string
  enrolled: boolean
  requested_satang: number
  conflict_of_interest: boolean
  conflict_note?: string
  ratings: { impact: number; need: number; feasibility: number; sustainability: number }
  status: string
  submitted_at: string
}

interface CSRAward {
  id: string
  program_id: string
  application_id: string
  reference_code: string
  full_name: string
  rank: number
  score_milli: number
  requested_satang: number
  award_satang: number
  status: 'awarded' | 'waitlisted'
  waitlist_reason?: string
  created_at: string
}

interface CSRDecision {
  rank: number
  application_id: string
  reference_code: string
  full_name: string
  score_milli: number
  requested_satang: number
  award_satang: number
  status: 'awarded' | 'waitlisted'
  waitlist_reason?: string
}

interface CSRReason {
  code: string
  message: string
}

interface CSRExclusion {
  application_id: string
  reference_code: string
  full_name: string
  reasons: CSRReason[]
}

interface CSRResult {
  program_id: string
  budget_satang: number
  max_award_satang: number
  min_award_satang: number
  allocated_satang: number
  unallocated_satang: number
  awarded_count: number
  waitlisted_count: number
  excluded_count: number
  decisions: CSRDecision[]
  exclusions: CSRExclusion[]
}

interface CSRAllocation {
  program: CSRProgram
  result: CSRResult
}

const PROGRAMS_KEY = ['csr-programs'] as const
const LEVELS = [
  { value: 'secondary', label: 'มัธยมศึกษา' },
  { value: 'vocational', label: 'อาชีวศึกษา' },
  { value: 'undergraduate', label: 'ปริญญาตรี' },
  { value: 'graduate', label: 'บัณฑิตศึกษา' },
]

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function parseBaht(raw: string): number | null {
  const s = raw.trim().replace(/,/g, '')
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return null
  const [whole, frac = ''] = s.split('.')
  const satang = Number(whole) * 100 + Number((frac + '00').slice(0, 2))
  return Number.isSafeInteger(satang) ? satang : null
}

function parseGPA(raw: string): number | null {
  const s = raw.trim()
  if (!/^\d(\.\d{0,2})?$/.test(s)) return null
  const value = Math.round(Number(s) * 100)
  return value >= 0 && value <= 400 ? value : null
}

function parseRating(raw: string): number | null {
  const s = raw.trim()
  if (!/^\d{1,3}$/.test(s)) return null
  const value = Number(s)
  return value >= 0 && value <= 100 ? value : null
}

function scoreLabel(milli: number): string {
  return (milli / 100).toFixed(2)
}

function programStatus(status: string): { label: string; tone: string } {
  switch (status) {
    case 'draft':
      return { label: 'ฉบับร่าง', tone: 'var(--color-text-dim)' }
    case 'open':
      return { label: 'เปิดรับสมัคร', tone: 'var(--color-emerald)' }
    case 'closed':
      return { label: 'ปิดรับสมัคร', tone: 'var(--color-amber)' }
    case 'allocated':
      return { label: 'จัดสรรแล้ว', tone: 'var(--color-blue)' }
    default:
      return { label: status, tone: 'var(--color-text-dim)' }
  }
}

function waitlistLabel(reason?: string): string {
  switch (reason) {
    case 'budget_exhausted':
      return 'งบประมาณไม่เพียงพอ'
    case 'below_minimum_award':
      return 'ต่ำกว่าทุนขั้นต่ำ'
    case 'no_score':
      return 'ไม่มีคะแนนถ่วงน้ำหนัก'
    case 'not_submitted':
      return 'ยังไม่ยื่นใบสมัคร'
    case 'conflict_of_interest':
      return 'ผลประโยชน์ทับซ้อน'
    default:
      return reason ?? ''
  }
}

export function CSR() {
  const qc = useQueryClient()
  const { me } = useAuth()
  const canWrite = me !== null && ['owner', 'admin', 'audit_manager'].includes(me.role)

  const programsQ = useQuery({
    queryKey: PROGRAMS_KEY,
    queryFn: () => list<CSRProgram>('/v1/csr/programs'),
    staleTime: 20_000,
  })
  const programs = programsQ.data ?? []
  const [selectedId, setSelectedId] = useState('')
  const selected = programs.find((p) => p.id === selectedId) ?? programs[0]

  const applicationsQ = useQuery({
    queryKey: ['csr-applications', selected?.id],
    queryFn: () => list<CSRApplication>(`/v1/csr/programs/${selected?.id}/applications`),
    enabled: Boolean(selected?.id),
    staleTime: 10_000,
  })
  const awardsQ = useQuery({
    queryKey: ['csr-awards', selected?.id],
    queryFn: () => list<CSRAward>(`/v1/csr/programs/${selected?.id}/awards`),
    enabled: Boolean(selected?.id),
    staleTime: 10_000,
  })

  const [lastRun, setLastRun] = useState<CSRAllocation | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // ------------------------------------------------------------------ program form
  const [name, setName] = useState('ทุนการศึกษาวิศวกรรมเพื่อชุมชน')
  const [description, setDescription] = useState('สนับสนุนนักศึกษาที่ทำโครงงานเพื่อชุมชน')
  const [budgetBaht, setBudgetBaht] = useState('1,000,000.00')
  const [capBaht, setCapBaht] = useState('180,000.00')
  const [minBaht, setMinBaht] = useState('150,000.00')
  const [impactPct, setImpactPct] = useState('35')
  const [needPct, setNeedPct] = useState('30')
  const [feasibilityPct, setFeasibilityPct] = useState('20')
  const [sustainabilityPct, setSustainabilityPct] = useState('15')
  const [minGPA, setMinGPA] = useState('3.00')
  const [maxIncomeBaht, setMaxIncomeBaht] = useState('300,000.00')
  const [level, setLevel] = useState('undergraduate')
  const [formError, setFormError] = useState<string | null>(null)

  const budgetSatang = parseBaht(budgetBaht)
  const capSatang = parseBaht(capBaht)
  const minSatang = parseBaht(minBaht)
  const incomeSatang = parseBaht(maxIncomeBaht)
  const gpaHundredths = parseGPA(minGPA)
  const weightTotal =
    (Number(impactPct) || 0) + (Number(needPct) || 0) +
    (Number(feasibilityPct) || 0) + (Number(sustainabilityPct) || 0)
  const programReady =
    name.trim().length > 0 && budgetSatang !== null && capSatang !== null && minSatang !== null &&
    incomeSatang !== null && gpaHundredths !== null && weightTotal === 100 &&
    budgetSatang > 0 && capSatang > 0 && minSatang >= 0 &&
    capSatang <= budgetSatang && minSatang <= capSatang

  const createProgram = useMutation({
    mutationFn: () =>
      apiWrite<CSRProgram>('/v1/csr/programs', 'POST', {
        name: name.trim(),
        description: description.trim(),
        budget_satang: budgetSatang ?? 0,
        max_award_satang: capSatang ?? 0,
        min_award_satang: minSatang ?? 0,
        weights: {
          impact_bp: Number(impactPct) * 100,
          need_bp: Number(needPct) * 100,
          feasibility_bp: Number(feasibilityPct) * 100,
          sustainability_bp: Number(sustainabilityPct) * 100,
        },
        rules: {
          nationality: 'TH',
          min_age: 0,
          max_age: 0,
          max_household_income_satang: incomeSatang ?? 0,
          min_gpa_hundredths: gpaHundredths ?? 0,
          levels: [level],
          require_enrollment: true,
        },
        opens_on: '',
        closes_on: '',
        status: 'open',
      }),
    onSuccess: (p) => {
      setSelectedId(p.id)
      setNotice(`สร้างโครงการ ${p.name} แล้ว`)
      setFormError(null)
      void qc.invalidateQueries({ queryKey: PROGRAMS_KEY })
    },
    onError: (err) => setFormError(errorText(err, 'สร้างโครงการไม่สำเร็จ')),
  })

  // ------------------------------------------------------------------ application form
  const [refCode, setRefCode] = useState('CSR-001')
  const [fullName, setFullName] = useState('')
  const [birthDate, setBirthDate] = useState('2005-01-15')
  const [gpa, setGpa] = useState('3.25')
  const [appLevel, setAppLevel] = useState('undergraduate')
  const [requestedBaht, setRequestedBaht] = useState('250,000.00')
  const [incomeAppBaht, setIncomeAppBaht] = useState('150,000.00')
  const [ratingImpact, setRatingImpact] = useState('80')
  const [ratingNeed, setRatingNeed] = useState('80')
  const [ratingFeasibility, setRatingFeasibility] = useState('80')
  const [ratingSustainability, setRatingSustainability] = useState('80')
  const [conflict, setConflict] = useState(false)
  const [conflictNote, setConflictNote] = useState('')
  const [appError, setAppError] = useState<string | null>(null)

  const requestedSatang = parseBaht(requestedBaht)
  const appIncomeSatang = parseBaht(incomeAppBaht)
  const appGPA = parseGPA(gpa)
  const ratingsValid = [ratingImpact, ratingNeed, ratingFeasibility, ratingSustainability]
    .every((raw) => parseRating(raw) !== null)
  const applicationReady =
    Boolean(selected) && refCode.trim().length > 0 && fullName.trim().length > 0 &&
    birthDate.length === 10 && requestedSatang !== null && requestedSatang > 0 &&
    appIncomeSatang !== null && appGPA !== null && ratingsValid

  const addApplication = useMutation({
    mutationFn: () =>
      apiWrite<CSRApplication[]>(`/v1/csr/programs/${selected?.id}/applications`, 'POST', {
        applications: [{
          reference_code: refCode.trim(),
          full_name: fullName.trim(),
          birth_date: birthDate,
          nationality: 'TH',
          household_income_satang: appIncomeSatang ?? 0,
          gpa_hundredths: appGPA ?? 0,
          study_level: appLevel,
          institution: '',
          enrolled: true,
          requested_satang: requestedSatang ?? 0,
          conflict_of_interest: conflict,
          conflict_note: conflict ? conflictNote.trim() : '',
          ratings: {
            impact: parseRating(ratingImpact) ?? 0,
            need: parseRating(ratingNeed) ?? 0,
            feasibility: parseRating(ratingFeasibility) ?? 0,
            sustainability: parseRating(ratingSustainability) ?? 0,
          },
          status: 'submitted',
        }],
      }),
    onSuccess: () => {
      setNotice(`เพิ่มใบสมัคร ${refCode.trim()} แล้ว`)
      setAppError(null)
      setFullName('')
      setRefCode((prev) => {
        const match = prev.match(/^(.*?)(\d+)$/)
        if (!match) return prev
        return `${match[1]}${String(Number(match[2]) + 1).padStart(match[2].length, '0')}`
      })
      void qc.invalidateQueries({ queryKey: ['csr-applications', selected?.id] })
      void qc.invalidateQueries({ queryKey: PROGRAMS_KEY })
    },
    onError: (err) => setAppError(errorText(err, 'เพิ่มใบสมัครไม่สำเร็จ')),
  })

  // ------------------------------------------------------------------ allocation
  const allocate = useMutation({
    mutationFn: () =>
      apiWrite<CSRAllocation>(`/v1/csr/programs/${selected?.id}/allocate`, 'POST'),
    onSuccess: (res) => {
      setLastRun(res)
      setNotice(`จัดสรรทุน ${res.program.name} แล้ว · ใช้ไป ${money(res.result.allocated_satang)}`)
      void qc.invalidateQueries({ queryKey: PROGRAMS_KEY })
      void qc.invalidateQueries({ queryKey: ['csr-awards', res.program.id] })
      void qc.invalidateQueries({ queryKey: ['csr-applications', res.program.id] })
    },
    onError: () => setNotice(null),
  })

  const apps = applicationsQ.data ?? []
  const awards = awardsQ.data ?? []
  const activeRun = lastRun && selected && lastRun.program.id === selected.id ? lastRun : null

  const weightChips = useMemo(() => {
    if (!selected) return []
    return [
      ['ผลกระทบทางสังคม', selected.weights.impact_bp],
      ['ความจำเป็นทางการเงิน', selected.weights.need_bp],
      ['ความเป็นไปได้', selected.weights.feasibility_bp],
      ['ความยั่งยืน', selected.weights.sustainability_bp],
    ] as [string, number][]
  }, [selected])

  return (
    <>
      <PageHead
        title="ทุนการศึกษาเพื่อสังคม"
        sub="คัดกรองคุณสมบัติ · ให้คะแนนตามน้ำหนักเกณฑ์ · จัดสรรงบประมาณเป็นสตางค์จำนวนเต็ม"
        actions={<span className="chip">{number(programs.length)} โครงการ</span>}
      />

      {notice ? (
        <div className="card card-pad" style={{ marginBottom: 16, borderColor: 'var(--color-emerald)' }}>
          <span style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="grid-main">
        <div className="card card-pad">
          <div className="row">
            <span className="card-label">โครงการทุนการศึกษา</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>เรียงตามวันที่สร้างล่าสุด</span>
          </div>
          {programsQ.isLoading ? (
            <div style={{ marginTop: 14 }}><Skel h={160} /></div>
          ) : programsQ.isError ? (
            <div className="form-error" style={{ marginTop: 14 }}>โหลดโครงการไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
          ) : programs.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <Empty
                title="ยังไม่มีโครงการทุนการศึกษา"
                body="สร้างโครงการใหม่เพื่อกำหนดงบประมาณ เพดานต่อราย และน้ำหนักเกณฑ์ให้คะแนน"
              />
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>โครงการ</th>
                    <th>สถานะ</th>
                    <th style={{ textAlign: 'right' }}>งบประมาณ</th>
                    <th style={{ textAlign: 'right' }}>ใช้ไป</th>
                    <th style={{ textAlign: 'right' }}>ผู้ได้รับทุน</th>
                  </tr>
                </thead>
                <tbody>
                  {programs.map((p) => {
                    const status = programStatus(p.status)
                    return (
                      <tr
                        key={p.id}
                        onClick={() => { setSelectedId(p.id); setLastRun(null) }}
                        style={{ cursor: 'pointer', background: selected?.id === p.id ? 'var(--color-surface-2)' : undefined }}
                      >
                        <td>
                          <div style={{ fontWeight: 500 }}>{p.name}</div>
                          <div className="dim" style={{ fontSize: 11.5 }}>{p.description || 'ไม่มีคำอธิบาย'}</div>
                        </td>
                        <td><span className="chip" style={{ color: status.tone }}>{status.label}</span></td>
                        <td className="num tnum" style={{ textAlign: 'right' }}>{money(p.budget_satang)}</td>
                        <td className="num tnum" style={{ textAlign: 'right' }}>
                          {p.status === 'allocated' ? money(p.allocated_satang) : <span className="dim">ยังไม่จัดสรร</span>}
                        </td>
                        <td className="num tnum" style={{ textAlign: 'right' }}>
                          {p.status === 'allocated' ? `${number(p.awarded_count)} ราย` : <span className="dim">—</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">สร้างโครงการใหม่</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
            น้ำหนักทั้งสี่เกณฑ์ต้องรวมกันเท่ากับ 100% เพดานต่อรายต้องไม่เกินงบประมาณรวม
            และทุนขั้นต่ำต้องไม่เกินเพดาน
          </div>
          <form
            style={{ marginTop: 12 }}
            onSubmit={(e) => { e.preventDefault(); if (canWrite && programReady) createProgram.mutate() }}
          >
            <div className="field">
              <label htmlFor="csr-name">ชื่อโครงการ</label>
              <input id="csr-name" className="input" value={name} disabled={!canWrite}
                onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="csr-desc">คำอธิบาย</label>
              <input id="csr-desc" className="input" value={description} disabled={!canWrite}
                onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="csr-budget">งบประมาณรวม (บาท)</label>
                <input id="csr-budget" className="input mono" inputMode="decimal" value={budgetBaht}
                  disabled={!canWrite} onChange={(e) => setBudgetBaht(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="csr-cap">เพดานต่อราย (บาท)</label>
                <input id="csr-cap" className="input mono" inputMode="decimal" value={capBaht}
                  disabled={!canWrite} onChange={(e) => setCapBaht(e.target.value)} />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="csr-min">ทุนขั้นต่ำ (บาท)</label>
                <input id="csr-min" className="input mono" inputMode="decimal" value={minBaht}
                  disabled={!canWrite} onChange={(e) => setMinBaht(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="csr-gpa">เกรดเฉลี่ยสะสมขั้นต่ำ</label>
                <input id="csr-gpa" className="input mono" inputMode="decimal" value={minGPA}
                  disabled={!canWrite} onChange={(e) => setMinGPA(e.target.value)} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="csr-income">รายได้ครัวเรือนสูงสุด (บาท)</label>
              <input id="csr-income" className="input mono" inputMode="decimal" value={maxIncomeBaht}
                disabled={!canWrite} onChange={(e) => setMaxIncomeBaht(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="csr-level">ระดับการศึกษาที่รับสมัคร</label>
              <select id="csr-level" className="input" value={level} disabled={!canWrite}
                onChange={(e) => setLevel(e.target.value)}>
                {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
            <div className="row" style={{ gap: 8 }}>
              {[
                ['ผลกระทบ', impactPct, setImpactPct],
                ['ความจำเป็นทางการเงิน', needPct, setNeedPct],
                ['ความเป็นไปได้', feasibilityPct, setFeasibilityPct],
                ['ความยั่งยืน', sustainabilityPct, setSustainabilityPct],
              ].map(([label, value, setter]) => (
                <div className="field" style={{ flex: 1 }} key={String(label)}>
                  <label htmlFor={`csr-w-${String(label)}`}>{String(label)} %</label>
                  <input id={`csr-w-${String(label)}`} className="input mono" inputMode="numeric"
                    value={String(value)} disabled={!canWrite}
                    onChange={(e) => (setter as (v: string) => void)(e.target.value)} />
                </div>
              ))}
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4, color: weightTotal === 100 ? 'var(--color-emerald)' : 'var(--color-crimson)' }}>
              น้ำหนักรวม {number(weightTotal)}% {weightTotal === 100 ? '· ครบถ้วน' : '· ต้องเท่ากับ 100%'}
            </div>
            <button className="btn btn-primary" type="submit" disabled={!canWrite || !programReady || createProgram.isPending}
              style={{ width: '100%', marginTop: 10 }}>
              {createProgram.isPending ? 'กำลังสร้าง…' : 'สร้างโครงการ'}
            </button>
          </form>
          {!canWrite ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              เฉพาะเจ้าของ ผู้ดูแลระบบ และผู้จัดการตรวจสอบเท่านั้นที่สร้างโครงการได้
            </div>
          ) : null}
          {formError ? <div className="form-error" style={{ marginTop: 10 }}>{formError}</div> : null}
          {budgetSatang !== null && capSatang !== null && capSatang > budgetSatang ? (
            <div className="form-error" style={{ marginTop: 10 }}>เพดานต่อรายต้องไม่เกินงบประมาณรวม</div>
          ) : null}
          {capSatang !== null && minSatang !== null && minSatang > capSatang ? (
            <div className="form-error" style={{ marginTop: 10 }}>ทุนขั้นต่ำต้องไม่เกินเพดานต่อราย</div>
          ) : null}
        </div>
      </div>

      {selected ? (
        <>
          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row">
              <div>
                <div className="card-label">{selected.name}</div>
                <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
                  {selected.description || 'ไม่มีคำอธิบาย'} · {programStatus(selected.status).label}
                </div>
              </div>
              <div className="row" style={{ marginLeft: 'auto', gap: 8, flexWrap: 'wrap' }}>
                {weightChips.map(([label, bp]) => (
                  <span key={label} className="chip">{label} {(bp / 100).toFixed(0)}%</span>
                ))}
              </div>
            </div>
            <div className="grid-4" style={{ marginTop: 16 }}>
              <Stat label="งบประมาณรวม" value={money(selected.budget_satang)} />
              <Stat label="เพดานต่อราย" value={money(selected.max_award_satang)} />
              <Stat label="ทุนขั้นต่ำ" value={money(selected.min_award_satang)} />
              <Stat
                label={selected.status === 'allocated' ? 'ใช้ไปแล้ว' : 'ผู้สมัคร'}
                value={selected.status === 'allocated' ? money(selected.allocated_satang) : number(apps.length)}
                foot={selected.status === 'allocated'
                  ? `${number(selected.awarded_count)} ได้รับ · ${number(selected.waitlisted_count)} สำรอง · ${number(selected.excluded_count)} คัดออก`
                  : 'ยังไม่ได้จัดสรร'}
              />
            </div>
            <div className="row" style={{ marginTop: 16, gap: 12 }}>
              <button className="btn btn-primary" type="button" disabled={!canWrite || allocate.isPending || apps.length === 0 || selected.status === 'draft'}
                onClick={() => { setNotice(null); allocate.mutate() }}>
                {allocate.isPending ? 'กำลังจัดสรร…' : selected.status === 'allocated' ? 'จัดสรรใหม่' : 'จัดสรรทุน'}
              </button>
              {selected.status === 'allocated' && selected.allocated_at ? (
                <span className="dim" style={{ fontSize: 11.5, alignSelf: 'center' }}>จัดสรรเมื่อ {thaiDate(selected.allocated_at)}</span>
              ) : null}
              {allocate.isError ? (
                <span className="form-error" style={{ alignSelf: 'center' }}>
                  {errorText(allocate.error, 'จัดสรรทุนไม่สำเร็จ')}
                </span>
              ) : null}
            </div>
            {selected.status === 'allocated' ? (
              <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.8 }}>
                การจัดสรรใหม่จะแทนที่ผลเดิมทั้งชุดในทรานแซกชันเดียว · งบประมาณคงเหลือ {money(Math.max(0, selected.budget_satang - selected.allocated_satang))}
              </div>
            ) : null}
          </div>

          <div className="grid-main" style={{ marginTop: 16 }}>
            <div className="card card-pad">
              <div className="row">
                <span className="card-label">ผู้สมัคร</span>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>{number(apps.length)} ใบสมัคร</span>
              </div>
              {applicationsQ.isLoading ? (
                <div style={{ marginTop: 14 }}><Skel h={150} /></div>
              ) : apps.length === 0 ? (
                <div style={{ marginTop: 14 }}>
                  <Empty title="ยังไม่มีใบสมัคร" body="เพิ่มใบสมัครทางแบบฟอร์มด้านขวา แล้วจึงจัดสรรทุนได้" />
                </div>
              ) : (
                <div className="table-wrap" style={{ marginTop: 12 }}>
                  <table className="data">
                    <thead>
                      <tr>
                        <th>รหัส</th>
                        <th>ผู้สมัคร</th>
                        <th>ระดับ</th>
                        <th style={{ textAlign: 'right' }}>เกรด</th>
                        <th style={{ textAlign: 'right' }}>ขอทุน</th>
                        <th>สถานะ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apps.map((a) => (
                        <tr key={a.id} style={{ cursor: 'default' }}>
                          <td className="mono" style={{ whiteSpace: 'nowrap' }}>{a.reference_code}</td>
                          <td>
                            <div>{a.full_name}</div>
                            {a.conflict_of_interest ? (
                              <div style={{ fontSize: 11, color: 'var(--color-crimson)' }}>ผลประโยชน์ทับซ้อน</div>
                            ) : null}
                          </td>
                          <td className="dim">{LEVELS.find((l) => l.value === a.study_level)?.label ?? a.study_level}</td>
                          <td className="num tnum" style={{ textAlign: 'right' }}>{(a.gpa_hundredths / 100).toFixed(2)}</td>
                          <td className="num tnum" style={{ textAlign: 'right' }}>{money(a.requested_satang)}</td>
                          <td>
                            <span className="chip" style={{ color: a.conflict_of_interest ? 'var(--color-crimson)' : 'var(--color-text-dim)' }}>
                              {a.conflict_of_interest ? 'คัดออก' : a.status === 'submitted' ? 'ยื่นแล้ว' : 'ถอนตัว'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card card-pad">
              <div className="card-label">เพิ่มใบสมัคร</div>
              <form
                style={{ marginTop: 12 }}
                onSubmit={(e) => { e.preventDefault(); if (canWrite && applicationReady) addApplication.mutate() }}
              >
                <div className="row" style={{ gap: 10 }}>
                  <div className="field" style={{ flex: 1 }}>
                    <label htmlFor="csr-ref">รหัสอ้างอิง</label>
                    <input id="csr-ref" className="input mono" value={refCode} disabled={!canWrite}
                      onChange={(e) => setRefCode(e.target.value)} />
                  </div>
                  <div className="field" style={{ flex: 2 }}>
                    <label htmlFor="csr-fullname">ชื่อผู้สมัคร</label>
                    <input id="csr-fullname" className="input" value={fullName} disabled={!canWrite}
                      onChange={(e) => setFullName(e.target.value)} />
                  </div>
                </div>
                <div className="row" style={{ gap: 10 }}>
                  <div className="field" style={{ flex: 1 }}>
                    <label htmlFor="csr-birth">วันเกิด</label>
                    <input id="csr-birth" className="input" type="date" value={birthDate}
                      disabled={!canWrite} onChange={(e) => setBirthDate(e.target.value)} />
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label htmlFor="csr-app-gpa">เกรดเฉลี่ยสะสม</label>
                    <input id="csr-app-gpa" className="input mono" inputMode="decimal" value={gpa}
                      disabled={!canWrite} onChange={(e) => setGpa(e.target.value)} />
                  </div>
                </div>
                <div className="row" style={{ gap: 10 }}>
                  <div className="field" style={{ flex: 1 }}>
                    <label htmlFor="csr-app-level">ระดับการศึกษา</label>
                    <select id="csr-app-level" className="input" value={appLevel} disabled={!canWrite}
                      onChange={(e) => setAppLevel(e.target.value)}>
                      {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label htmlFor="csr-requested">ขอทุน (บาท)</label>
                    <input id="csr-requested" className="input mono" inputMode="decimal" value={requestedBaht}
                      disabled={!canWrite} onChange={(e) => setRequestedBaht(e.target.value)} />
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="csr-app-income">รายได้ครัวเรือน (บาท)</label>
                  <input id="csr-app-income" className="input mono" inputMode="decimal" value={incomeAppBaht}
                    disabled={!canWrite} onChange={(e) => setIncomeAppBaht(e.target.value)} />
                </div>
                <div className="row" style={{ gap: 8 }}>
                  {[
                    ['ผลกระทบ', ratingImpact, setRatingImpact],
                    ['ความจำเป็นทางการเงิน', ratingNeed, setRatingNeed],
                    ['ความเป็นไปได้', ratingFeasibility, setRatingFeasibility],
                    ['ความยั่งยืน', ratingSustainability, setRatingSustainability],
                  ].map(([label, value, setter]) => (
                    <div className="field" style={{ flex: 1 }} key={String(label)}>
                      <label htmlFor={`csr-r-${String(label)}`}>{String(label)} /100</label>
                      <input id={`csr-r-${String(label)}`} className="input mono" inputMode="numeric"
                        value={String(value)} disabled={!canWrite}
                        onChange={(e) => (setter as (v: string) => void)(e.target.value)} />
                    </div>
                  ))}
                </div>
                <label className="row" style={{ gap: 8, marginTop: 8, fontSize: 12 }}>
                  <input type="checkbox" checked={conflict} disabled={!canWrite}
                    onChange={(e) => setConflict(e.target.checked)} />
                  มีผลประโยชน์ทับซ้อน (คัดออกก่อนจัดอันดับ)
                </label>
                {conflict ? (
                  <div className="field" style={{ marginTop: 8 }}>
                    <label htmlFor="csr-conflict-note">หมายเหตุ</label>
                    <input id="csr-conflict-note" className="input" value={conflictNote} disabled={!canWrite}
                      onChange={(e) => setConflictNote(e.target.value)} />
                  </div>
                ) : null}
                <button className="btn btn-primary" type="submit" disabled={!canWrite || !applicationReady || addApplication.isPending}
                  style={{ width: '100%', marginTop: 10 }}>
                  {addApplication.isPending ? 'กำลังเพิ่ม…' : 'เพิ่มใบสมัคร'}
                </button>
              </form>
              {appError ? <div className="form-error" style={{ marginTop: 10 }}>{appError}</div> : null}
              {!applicationReady && fullName.length > 0 ? (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
                  ตรวจสอบรหัส ชื่อ วันเกิด จำนวนทุน และคะแนนทั้งสี่เกณฑ์ (0–100)
                </div>
              ) : null}
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row">
              <span className="card-label">ผลการจัดสรร</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                {activeRun ? 'ผลจากรอบล่าสุดที่เรียกในหน้านี้' : 'อ่านจากทะเบียนที่บันทึกไว้'}
              </span>
            </div>
            {awardsQ.isLoading ? (
              <div style={{ marginTop: 14 }}><Skel h={140} /></div>
            ) : activeRun ? (
              <>
                <div className="grid-4" style={{ marginTop: 16 }}>
                  <Stat label="จัดสรรแล้ว" value={money(activeRun.result.allocated_satang)} />
                  <Stat label="งบประมาณคงเหลือ" value={money(activeRun.result.unallocated_satang)} />
                  <Stat label="ได้รับทุน" value={`${number(activeRun.result.awarded_count)} ราย`} />
                  <Stat label="รายการสำรอง" value={`${number(activeRun.result.waitlisted_count)} ราย`} />
                </div>
                <AllocationTable decisions={activeRun.result.decisions} />
                {activeRun.result.exclusions.length > 0 ? (
                  <div style={{ marginTop: 16 }}>
                    <div className="card-label" style={{ marginBottom: 8 }}>ผู้ถูกคัดออก</div>
                    {activeRun.result.exclusions.map((x) => (
                      <div key={x.application_id} style={{ fontSize: 12, padding: '6px 0', borderTop: '1px solid var(--color-border)' }}>
                        <span className="mono">{x.reference_code}</span> · {x.full_name}
                        <span className="dim"> — {x.reasons.map((reason) => reason.message).join(' · ')}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : awards.length === 0 ? (
              <div style={{ marginTop: 14 }}>
                <Empty
                  title="ยังไม่มีผลการจัดสรร"
                  body="เมื่อมีใบสมัครแล้ว กดจัดสรรทุน ระบบจะบันทึกอันดับ คะแนน และจำนวนทุนที่ได้รับ"
                />
              </div>
            ) : (
              <AllocationTable decisions={awards.map((a) => ({
                rank: a.rank,
                application_id: a.application_id,
                reference_code: a.reference_code,
                full_name: a.full_name,
                score_milli: a.score_milli,
                requested_satang: a.requested_satang,
                award_satang: a.award_satang,
                status: a.status,
                waitlist_reason: a.waitlist_reason,
              }))} />
            )}
          </div>
        </>
      ) : null}
    </>
  )
}

function AllocationTable({ decisions }: { decisions: CSRDecision[] }) {
  if (decisions.length === 0) {
    return <div className="dim" style={{ marginTop: 14, fontSize: 12 }}>ไม่มีผู้เข้าสู่การจัดอันดับ</div>
  }
  return (
    <div className="table-wrap" style={{ marginTop: 12 }}>
      <table className="data">
        <thead>
          <tr>
            <th style={{ textAlign: 'right' }}>อันดับ</th>
            <th>ผู้สมัคร</th>
            <th>รหัส</th>
            <th style={{ textAlign: 'right' }}>คะแนน</th>
            <th style={{ textAlign: 'right' }}>ขอทุน</th>
            <th style={{ textAlign: 'right' }}>ได้รับทุน</th>
            <th>สถานะ</th>
          </tr>
        </thead>
        <tbody>
          {decisions.map((d) => (
            <tr key={d.application_id} style={{ cursor: 'default' }}>
              <td className="num tnum" style={{ textAlign: 'right' }}>{number(d.rank)}</td>
              <td>{d.full_name}</td>
              <td className="mono" style={{ whiteSpace: 'nowrap' }}>{d.reference_code}</td>
              <td className="num tnum" style={{ textAlign: 'right' }}>{scoreLabel(d.score_milli)}</td>
              <td className="num tnum" style={{ textAlign: 'right' }}>{money(d.requested_satang)}</td>
              <td className="num tnum" style={{ textAlign: 'right', color: d.status === 'awarded' ? 'var(--color-emerald)' : undefined }}>
                {d.status === 'awarded' ? money(d.award_satang) : <span className="dim">—</span>}
              </td>
              <td>
                {d.status === 'awarded' ? (
                  <span className="chip" style={{ color: 'var(--color-emerald)' }}>ได้รับทุน</span>
                ) : (
                  <span className="chip" style={{ color: 'var(--color-amber)' }}>สำรอง · {waitlistLabel(d.waitlist_reason)}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
